import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Shield, Zap, Radar } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login, register, formatApiErrorDetail } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [workspaceName, setWorkspaceName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      if (mode === "login") {
        await login(email, password);
        toast.success("Authenticated");
      } else {
        await register({ email, password, name, workspace_name: workspaceName });
        toast.success("Workspace provisioned");
      }
      navigate("/", { replace: true });
    } catch (e) {
      const msg = formatApiErrorDetail(e?.response?.data?.detail) || e.message;
      setErr(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen w-full grid grid-cols-1 lg:grid-cols-2 relative overflow-hidden">
      {/* Left / brand pane */}
      <div className="hidden lg:flex relative flex-col justify-between p-12 border-r border-[color:var(--border)] overflow-hidden">
        <div
          className="absolute inset-0 grid-bg opacity-40 pointer-events-none"
          aria-hidden
        />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(600px circle at 20% 30%, rgba(0,240,255,0.08), transparent 60%)",
          }}
        />
        <div className="relative z-10 flex items-center gap-3">
          <div className="w-9 h-9 border border-[color:var(--accent)] flex items-center justify-center text-[color:var(--accent)]">
            <Radar size={18} strokeWidth={1.5} />
          </div>
          <div>
            <div className="text-sm tracking-[0.32em] mono text-[color:var(--text-2)]">
              IVOROVI
            </div>
            <div className="text-xl font-medium tracking-tight">Revora</div>
          </div>
        </div>

        <div className="relative z-10 space-y-6">
          <div className="eyebrow">/ legacy modernization intelligence</div>
          <h1 className="text-4xl xl:text-5xl font-medium tracking-tight leading-[1.05]">
            Turn weeks of manual audit
            <br />
            into a{" "}
            <span className="text-[color:var(--accent)]">signed roadmap</span>{" "}
            in hours.
          </h1>
          <p className="text-[color:var(--text-2)] max-w-md">
            Scan → Diagnose → Prescribe → Transform → Sustain. One repeatable
            engine your consultants run for every engagement.
          </p>
          <div className="grid grid-cols-2 gap-3 max-w-md mono text-xs">
            {[
              ["01", "SCAN", "auto-crawl code, data, infra"],
              ["02", "DIAGNOSE", "risk × debt × criticality"],
              ["03", "PRESCRIBE", "wrap · rewrite · replatform"],
              ["04", "TRANSFORM", "kanban migration board"],
            ].map(([n, k, v]) => (
              <div
                key={n}
                className="border border-[color:var(--border)] p-3 bg-[color:var(--bg-1)]"
              >
                <div className="text-[color:var(--accent)]">{n}</div>
                <div className="text-white mt-1">{k}</div>
                <div className="text-[color:var(--text-3)] text-[10px] mt-1 uppercase tracking-widest">
                  {v}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 mono text-[10px] uppercase tracking-widest text-[color:var(--text-3)] flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <span className="w-1.5 h-1.5 bg-[color:var(--success)]" />
            system online · v0.1 · multi-tenant
          </div>
          <a
            href="https://www.ivorovi.com"
            target="_blank"
            rel="noreferrer"
            data-testid="footer-website"
            className="text-[color:var(--text-2)] hover:text-[color:var(--accent)] transition-colors duration-150"
          >
            www.ivorovi.com
          </a>
        </div>
      </div>

      {/* Right / form pane */}
      <div className="flex items-center justify-center p-6 lg:p-12 relative">
        <div className="w-full max-w-md space-y-8">
          <div className="lg:hidden flex items-center gap-3 mb-6">
            <div className="w-9 h-9 border border-[color:var(--accent)] flex items-center justify-center text-[color:var(--accent)]">
              <Radar size={18} strokeWidth={1.5} />
            </div>
            <div className="text-lg font-medium">IVOROVI Revora</div>
          </div>

          <div>
            <div className="eyebrow mb-2">/ access console</div>
            <h2 className="text-3xl font-medium tracking-tight">
              {mode === "login" ? "Sign in" : "Create workspace"}
            </h2>
            <p className="text-[color:var(--text-2)] text-sm mt-2">
              {mode === "login"
                ? "Continue to your modernization workspace."
                : "Provision an isolated tenant for your consulting engagement."}
            </p>
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
            {mode === "register" && (
              <>
                <Field
                  label="Your name"
                  testId="reg-name"
                  value={name}
                  onChange={setName}
                  placeholder="Jane Doe"
                  required
                />
                <Field
                  label="Workspace / client name"
                  testId="reg-workspace"
                  value={workspaceName}
                  onChange={setWorkspaceName}
                  placeholder="Acme Bank Modernization"
                  required
                />
              </>
            )}
            <Field
              label="Email"
              testId="auth-email"
              type="email"
              value={email}
              onChange={setEmail}
              placeholder="**********"
              required
            />
            <Field
              label="Password"
              testId="auth-password"
              type="password"
              value={password}
              onChange={setPassword}
              placeholder="**********"
              required
            />

            {err && (
              <div
                data-testid="auth-error"
                className="mono text-xs text-[color:var(--critical)] border border-[color:var(--critical)]/40 bg-[color:var(--critical)]/5 p-2"
              >
                ! {err}
              </div>
            )}

            <button
              data-testid="auth-submit"
              type="submit"
              disabled={busy}
              className="w-full h-11 bg-[color:var(--accent)] text-black font-medium tracking-tight hover:bg-[color:var(--accent-hover)] transition-colors duration-150 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {busy ? (
                <span className="mono text-xs">executing…</span>
              ) : (
                <>
                  <Zap size={16} strokeWidth={1.75} />
                  {mode === "login" ? "Sign in" : "Provision workspace"}
                </>
              )}
            </button>
          </form>

          <div className="flex items-center justify-between text-xs mono">
            <span className="text-[color:var(--text-3)]">
              Access is invite-only
            </span>
            <span
              data-testid="auth-registration-disabled"
              className="mono text-[10px] uppercase tracking-widest text-[color:var(--text-3)]"
            >
              contact your admin
            </span>
          </div>

          <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--text-3)] pt-6 border-t border-[color:var(--border)] space-y-2">
            <div className="flex items-center gap-2">
              <Shield size={12} strokeWidth={1.5} />
              secure workspace · encrypted at rest
            </div>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <span data-testid="auth-copyright">© 2026 IVOROVI. All rights reserved.</span>
              <a
                href="https://www.ivorovi.com"
                target="_blank"
                rel="noreferrer"
                className="text-[color:var(--text-2)] hover:text-[color:var(--accent)] transition-colors duration-150"
              >
                www.ivorovi.com
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, testId, value, onChange, type = "text", ...rest }) {
  return (
    <label className="block">
      <span className="mono text-[10px] uppercase tracking-widest text-[color:var(--text-2)]">
        {label}
      </span>
      <input
        data-testid={testId}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1.5 w-full h-11 bg-[color:var(--bg-base)] border border-[color:var(--border)] px-3 mono text-sm text-white placeholder:text-[color:var(--text-3)] focus:border-[color:var(--accent)] focus:outline-none focus:ring-1 focus:ring-[color:var(--accent)] transition-colors duration-150"
        {...rest}
      />
    </label>
  );
}
